import { RefetchQueriesFunction, useMutation } from '@apollo/client';
import DateTimeLabel from '@monorepo/common/component/DateTimeLabel';
import { IconSolidSync, IconSolidUndo } from '@monorepo/common/component/Icons';
import MoneyLabel from '@monorepo/common/component/MoneyLabel';
import PropTypes from 'prop-types';
import React, { MouseEventHandler, ReactNode, useCallback, useState } from 'react';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row, Table } from 'reactstrap';
import {
  BasicPersonFragment,
  GatewayTransactionCsobRefreshErrorEnum,
  GatewayTransactionCsobStatusEnum,
  RefundTransactionDocument,
  ReverseTransactionDocument,
  UpdateTransactionDocument
} from '../../../../graphql-operations';
import PersonSwitchNameLink from '../../../person/switch/PersonSwitchNameLink';
import OrderTransactionRefundModal, { useOrderTransactionRefundModal } from '../OrderTransactionRefundModal';
import TransactionRefreshError from './TransactionRefreshError';
import TransactionStatus from './TransactionStatus';

interface Revision {
  id: string;
  metadata: {
    id: string;
    dateTime: string;
    person: BasicPersonFragment;
  };
  entity: {
    refundReason?: string | null;
    status: {
      type: GatewayTransactionCsobStatusEnum;
    };
  };
}

interface OrderTransactionRevisionsListProps {
  revisions: Revision[];
}

function OrderTransactionRevisionsList({ revisions }: OrderTransactionRevisionsListProps) {
  return (
    <Table size="sm" striped>
      <thead>
        <tr>
          <th>Datum</th>
          <th>Stav</th>
          <th>Autor</th>
        </tr>
      </thead>
      <tbody>
        {revisions.map(revision => {
          return (
            <tr key={revision.id}>
              <td>
                <DateTimeLabel dateTimeString={revision.metadata.dateTime} />
              </td>
              <td>
                <TransactionStatus status={revision.entity.status.type}></TransactionStatus>
                {revision.entity.refundReason && <div>{revision.entity.refundReason}</div>}
              </td>
              <td>
                <PersonSwitchNameLink person={revision.metadata.person} />
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
}

interface Refresh {
  id: string;
  created: string;
  statusCsob?: GatewayTransactionCsobStatusEnum | null;
  statusDbBefore: GatewayTransactionCsobStatusEnum;
  statusDbAfter: GatewayTransactionCsobStatusEnum;
  error?: GatewayTransactionCsobRefreshErrorEnum | null;
  errorMessage?: string | null;
}

interface OrderTransactionRefreshesListProps {
  refreshes: Refresh[];
}

function OrderTransactionRefreshesList({ refreshes }: OrderTransactionRefreshesListProps) {
  const [errorMessage, setErrorMessage] = useState<Refresh['errorMessage']>(null);
  const [modalOpened, setModalOpened] = useState(false);

  return (
    <>
      <DetailedErrorModal errorMessage={errorMessage} isOpen={modalOpened} close={() => setModalOpened(false)} />

      <Table size="sm" striped>
        <thead>
          <tr>
            <th>Datum</th>
            <th>Stav v ČSOB</th>
            <th>Stav v DB před</th>
            <th>Stav v DB po</th>
            <th colSpan={2}>Chyba</th>
          </tr>
        </thead>
        <tbody>
          {refreshes.map(refresh => {
            const openModal = () => {
              setErrorMessage(refresh.errorMessage);
              setModalOpened(true);
            };

            return (
              <tr key={refresh.id}>
                <td>
                  <DateTimeLabel dateTimeString={refresh.created} />
                </td>
                <td>{refresh.statusCsob && <TransactionStatus status={refresh.statusCsob}></TransactionStatus>}</td>
                <td>
                  {refresh.statusDbBefore && <TransactionStatus status={refresh.statusDbBefore}></TransactionStatus>}
                </td>
                <td>
                  {refresh.statusDbAfter && <TransactionStatus status={refresh.statusDbAfter}></TransactionStatus>}
                </td>
                <td className="text-danger">{refresh.error && <TransactionRefreshError error={refresh.error} />}</td>
                <td>{refresh.errorMessage && <Button onClick={openModal}>Více</Button>}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </>
  );
}

interface DetailedErrorModalProps {
  errorMessage: Refresh['errorMessage'];
  isOpen: boolean;
  close: () => void;
}

function DetailedErrorModal({ errorMessage, isOpen, close }: DetailedErrorModalProps) {
  return (
    <Modal isOpen={isOpen} size="lg" toggle={close} centered>
      <ModalHeader toggle={close}>Detail chyby</ModalHeader>
      <ModalBody>
        <div className="text-danger small mt-1" style={{ wordBreak: 'break-all' }}>
          {errorMessage}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button onClick={close} className="MutResult-auto">
          Zavřít
        </Button>
      </ModalFooter>
    </Modal>
  );
}

interface InfoRowProps {
  label: string;
  children: ReactNode;
}

const InfoRow = ({ label, children }: InfoRowProps) => {
  return (
    <Row className="mb-1">
      <Col xs={3}>
        <span className="text-muted">{label}</span>
      </Col>
      <Col xs={9}>{children}</Col>
    </Row>
  );
};

interface RefundTransactionIconProps {
  refundMutation: typeof RefundTransactionDocument | typeof ReverseTransactionDocument;
  transactionId: string;
  amount: number;
  refetchQueries: RefetchQueriesFunction;
}

function RefundTransactionIcon({ refundMutation, transactionId, amount, refetchQueries }: RefundTransactionIconProps) {
  const { modalProps, openModal } = useOrderTransactionRefundModal(refundMutation, transactionId, refetchQueries);

  return (
    <>
      <IconSolidUndo onClick={openModal} title="Vrácení peněz" className="action-icon text-warning" />
      <OrderTransactionRefundModal {...modalProps} amount={amount} />
    </>
  );
}

export interface TransactionCsob {
  id: string;
  payId: string;
  amount: number;
  refundReason?: string | null;
  refreshCount?: number | null;
  refreshNextAttemptAfter?: string | null;
  status: {
    type: GatewayTransactionCsobStatusEnum;
    reversible: boolean;
    refundable: boolean;
  };
  revisions: Revision[];
  refreshes: Refresh[];
}

export interface OrderTransactionCsobDetailProps {
  transaction: TransactionCsob;
  refetchQueries: RefetchQueriesFunction;
}

export default function OrderTransactionCsobDetail({ transaction, refetchQueries }: OrderTransactionCsobDetailProps) {
  const [updateTransaction] = useMutation(UpdateTransactionDocument, {
    refetchQueries
  });

  const refundMutation = transaction.status.reversible
    ? ReverseTransactionDocument
    : transaction.status.refundable
    ? RefundTransactionDocument
    : undefined;

  const onUpdateTransactionClick = useCallback<MouseEventHandler>(
    e => {
      e.preventDefault();
      updateTransaction({
        variables: {
          id: transaction.id
        }
      });
    },
    [updateTransaction, transaction.id]
  );

  return (
    <>
      <InfoRow label="PayId">{transaction.payId}</InfoRow>

      <InfoRow label="Stav">
        <TransactionStatus status={transaction.status.type} />{' '}
        <IconSolidSync onClick={onUpdateTransactionClick} title="Aktualizovat" className="action-icon text-primary" />
      </InfoRow>

      {transaction.refundReason && (
        <InfoRow label="Důvod vrácení peněz">
          <span>{transaction.refundReason}</span>
        </InfoRow>
      )}

      <InfoRow label="Částka">
        <MoneyLabel amount={transaction.amount} />{' '}
        {refundMutation && (
          <RefundTransactionIcon
            refundMutation={refundMutation}
            transactionId={transaction.id}
            amount={transaction.amount}
            refetchQueries={refetchQueries}
          />
        )}
      </InfoRow>

      <h6 className="mt-3 mb-2">Historie stavů</h6>
      <Row className="mb-1">
        <Col sm={12}>
          <OrderTransactionRevisionsList revisions={[...transaction.revisions].reverse()} />
        </Col>
      </Row>

      {(transaction.refreshCount != null || transaction.refreshes.length > 0) && (
        <>
          <h6 className="mt-3">Automatické aktualizace</h6>
          {transaction.refreshCount != null && (
            <>
              <InfoRow label="Počet pokusů">
                <span className="text-warning">{transaction.refreshCount}×</span>
              </InfoRow>

              {transaction.refreshNextAttemptAfter && (
                <InfoRow label="Příští pokus">
                  <DateTimeLabel dateTimeString={transaction.refreshNextAttemptAfter} />
                </InfoRow>
              )}
            </>
          )}

          <Row className="mb-1">
            <Col sm={12} className="mt-2">
              <OrderTransactionRefreshesList refreshes={[...transaction.refreshes].reverse()} />
            </Col>
          </Row>
        </>
      )}
    </>
  );
}

OrderTransactionCsobDetail.propTypes = {
  transaction: PropTypes.shape({
    id: PropTypes.string.isRequired,
    payId: PropTypes.string.isRequired,
    status: PropTypes.shape({
      type: PropTypes.string.isRequired,
      reversible: PropTypes.bool.isRequired,
      refundable: PropTypes.bool.isRequired
    }).isRequired,
    amount: PropTypes.number.isRequired
  }).isRequired,
  refetchQueries: PropTypes.func
};
